import React, { Suspense, lazy, useState, useEffect, useRef, useCallback } from "react";
import i18n from "./i18n";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
  Outlet,
} from "react-router-dom";
import SideCart from "./components/SideCart"; // 添加侧边购物车组件
import PasswordProtectedRoute from "./components/PasswordProtectedRoute";
import EmailOtpProtectedRoute from "./components/EmailOtpProtectedRoute";
import RouteSeoHead from "./components/RouteSeoHead";
import { MARKET_OPTIONS, getMarketByCountry } from "./utils/shopifyMarkets";
import {
  getClarityPageGroup,
  getClarityPageTemplate,
  isClaritySensitivePath,
} from "./utils/clarity";
import {
  buildPageAnalyticsContext,
  getCartValueBucket,
  setAnalyticsContext,
  trackAnalyticsEvent,
  trackAnalyticsPageView,
} from "./utils/analytics";
import {
  DEFAULT_LOCALE,
  getLocaleFromPathname,
  isSupportedLocale,
  localeToHtmlLang,
  localeToUrlSegment,
  localizePath,
  normalizeLocale,
  stripLocalePrefix,
} from "./utils/localeRouting";
import {
  getAnalyticsConsentState,
  syncAnalyticsIntegrations,
  trackMetaPixelPageView,
  ensureClarityLoaded,
} from "./utils/tracking";
import { logLukkeyDebug } from "./utils/lukkeyDebug";

const Home = lazy(() => import("./pages/Home"));
const GlobalButtonMotion = lazy(() => import("./components/GlobalButtonMotion"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const WarrantyPolicy = lazy(() => import("./pages/WarrantyPolicy"));
const Compliance = lazy(() => import("./pages/Compliance"));
const AppDownload = lazy(() => import("./pages/AppDownload"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const WarrantySupport = lazy(() => import("./pages/WarrantySupport"));
const InternationalDelivery = lazy(() => import("./pages/InternationalDelivery"));
const Community = lazy(() => import("./pages/Community"));
const MediaKit = lazy(() => import("./pages/MediaKit"));
const ThreeDRender = lazy(() => import("./pages/ThreeDRender"));
const SupportedCryptoAssets = lazy(() => import("./pages/SupportedCryptoAssets"));
const PartnerPrograms = lazy(() => import("./pages/PartnerPrograms"));
const PartnerInquiry = lazy(() => import("./pages/PartnerInquiry"));
const CoBranding = lazy(() => import("./pages/CoBranding"));
const Lukkey = lazy(() => import("./pages/Lukkey"));
const LukkeyIssuesPage = lazy(() => import("./pages/LukkeyIssues.js"));
const FinancialModelPage = lazy(() => import("./pages/FinancialModel.js"));
const SubscribeConfirmed = lazy(() => import("./pages/SubscribeConfirmed"));
const PurchaseSuccess = lazy(() => import("./pages/PurchaseSuccess"));
const NotFound = lazy(() => import("./pages/NotFound"));
const R2ManagerPage = lazy(() => import("./pages/R2Manager.js"));
const AdminAuthPage = lazy(() => import("./pages/AdminAuth.js"));
const Brc20Inspector = lazy(() => import("./pages/Brc20Inspector.js"));
const JaimeCalendar = lazy(() => import("./pages/JaimeCalendar.js"));
const AcademyArticle = lazy(() => import("./pages/AcademyArticle"));
const AcademyAdminPage = lazy(() => import("./pages/AcademyAdmin.js"));
const CostDistributionAdmin = lazy(() => import("./pages/CostDistributionAdmin.js"));
const InternalTypographyAudit = lazy(() => import("./pages/InternalTypographyAudit.js"));
const BarcodeGenerator = lazy(() => import("./pages/BarcodeGenerator.js"));

const isLukkeyDebugPath = (pathname = "") => {
  const normalizedPath = stripLocalePrefix(pathname || "");
  return normalizedPath === "/lukkey" || normalizedPath === "/pre";
};

const logLukkeyRouteDebug = (scope, message, payload) => {
  if (typeof window === "undefined") return;
  if (!isLukkeyDebugPath(window.location?.pathname || "")) return;
  logLukkeyDebug(scope, message, payload);
};

function NoIndex() {
  useEffect(() => {
    if (typeof document === "undefined") return;
    const metaNames = ["robots", "googlebot", "bingbot"];
    const previous = new Map();
    const metas = metaNames.map((name) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      previous.set(name, meta ? meta.getAttribute("content") : null);
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", name);
        document.head.appendChild(meta);
      }
      meta.setAttribute(
        "content",
        "noindex, nofollow, noarchive, nosnippet, noimageindex"
      );
      return meta;
    });
    return () => {
      metas.forEach((meta) => {
        if (!meta) return;
        const name = meta.getAttribute("name");
        const prev = previous.get(name);
        if (prev !== null && prev !== undefined) {
          meta.setAttribute("content", prev);
        } else {
          try {
            document.head.removeChild(meta);
          } catch {}
        }
      });
    };
  }, []);
  return null;
}

function SensitiveRouteShield({ children }) {
  useEffect(() => {
    if (typeof document === "undefined") return;
    try {
      document.documentElement.setAttribute("data-nosnippet", "true");
    } catch {}

    return () => {
      try {
        document.documentElement.removeAttribute("data-nosnippet");
      } catch {}
    };
  }, []);

  return (
    <>
      <NoIndex />
      <div data-clarity-mask="true" style={{ display: "contents" }}>
        {children}
      </div>
    </>
  );
}

function ClarityRouteTracker({ analyticsConsent }) {
  const location = useLocation();
  useEffect(() => {
    logLukkeyRouteDebug("App.ClarityRouteTracker", "location changed", {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    });
    if (typeof window === "undefined") return;
    const path = `${location.pathname}${location.search}${location.hash}`;
    const normalizedPath = stripLocalePrefix(location.pathname);
    const routeLocale =
      getLocaleFromPathname(location.pathname) || DEFAULT_LOCALE;
    const isSensitive = isClaritySensitivePath(location.pathname);
    const pageContext = buildPageAnalyticsContext(location.pathname, {
      locale: routeLocale,
      page_group: getClarityPageGroup(normalizedPath) || "other",
      page_template: getClarityPageTemplate(normalizedPath) || normalizedPath,
      page_path_base: normalizedPath,
    });

    syncAnalyticsIntegrations({
      pathname: location.pathname,
      consentState: analyticsConsent,
    });

    if (isSensitive) {
      return;
    }

    const clarity = ensureClarityLoaded(location.pathname) || window.clarity;
    if (typeof clarity !== "function") return;
    clarity("set", "page", path);
    setAnalyticsContext(pageContext);
    const pageViewRaf = window.requestAnimationFrame(() => {
      trackAnalyticsPageView(pageContext);
    });
    trackMetaPixelPageView();
    return () => window.cancelAnimationFrame(pageViewRaf);
  }, [location, analyticsConsent]);
  return null;
}

function LocaleStateSync({ locale = DEFAULT_LOCALE }) {
  useEffect(() => {
    const normalizedLocale = normalizeLocale(locale);
    logLukkeyRouteDebug("App.LocaleStateSync", "sync requested", {
      locale,
      normalizedLocale,
      resolvedLanguage: i18n.resolvedLanguage,
      language: i18n.language,
    });

    if (i18n.resolvedLanguage !== normalizedLocale) {
      logLukkeyRouteDebug("App.LocaleStateSync", "calling i18n.changeLanguage", {
        from: i18n.resolvedLanguage,
        to: normalizedLocale,
      });
      i18n.changeLanguage(normalizedLocale);
    }

    if (typeof document !== "undefined") {
      document.documentElement.lang = localeToHtmlLang(normalizedLocale);
    }

    if (typeof window !== "undefined") {
      try {
        localStorage.setItem("i18n_lang", normalizedLocale);
      } catch {}
    }
  }, [locale]);

  return null;
}

function OptionalLocaleBoundary() {
  const { locale: localeParam } = useParams();
  const location = useLocation();

  if (localeParam && !isSupportedLocale(localeParam)) {
    return <NotFound />;
  }

  const normalizedLocale = localeParam
    ? normalizeLocale(localeParam)
    : DEFAULT_LOCALE;
  const canonicalSegment = localeToUrlSegment(normalizedLocale);

  if (
    localeParam &&
    String(localeParam || "").toLowerCase() !== canonicalSegment
  ) {
    const target = localizePath(
      `${stripLocalePrefix(location.pathname)}${location.search}${location.hash}`,
      normalizedLocale
    );
    logLukkeyRouteDebug(
      "App.OptionalLocaleBoundary",
      "redirecting to canonical locale segment",
      {
        localeParam,
        normalizedLocale,
        canonicalSegment,
        from: `${location.pathname}${location.search}${location.hash}`,
        to: target,
      }
    );
    return <Navigate to={target} replace />;
  }

  const basePath = stripLocalePrefix(location.pathname);
  if (basePath.toLowerCase() === "/social" && basePath !== "/social") {
    return (
      <Navigate
        to={localizePath(
          `/social${location.search}${location.hash}`,
          normalizedLocale
        )}
        replace
      />
    );
  }

  return (
    <>
      <LocaleStateSync locale={normalizedLocale} />
      <Outlet />
    </>
  );
}

function ShippingAliasRedirect() {
  const { page } = useParams();
  const location = useLocation();
  const currentLocale =
    getLocaleFromPathname(location.pathname) || DEFAULT_LOCALE;
  const normalizedPage = Number.parseInt(page || "1", 10);
  const safePage = Number.isFinite(normalizedPage) && normalizedPage > 0 ? normalizedPage : 1;

  return (
    <Navigate
      to={localizePath(`/shipping/page/${safePage}`, currentLocale)}
      replace
    />
  );
}

function App() {
  const isReserveItem = (item) =>
    String(item?.cartKey || "").startsWith("reserve::");

  const [selectedCountry, setSelectedCountry] = useState(() => {
    if (typeof window === "undefined") return "US";
    return localStorage.getItem("lukkey_selected_country") || "US";
  });
  const analyticsConsent = getAnalyticsConsentState();

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    let autoFieldIdSeed = 0;
    const fieldSelector = "input, select, textarea";

    const ensureFormFieldIdentity = (rootNode) => {
      if (!rootNode) return;
      const scope =
        rootNode instanceof Element || rootNode instanceof Document
          ? rootNode
          : document;
      const fields = scope.querySelectorAll(fieldSelector);
      fields.forEach((field) => {
        if (field.hasAttribute("id") || field.hasAttribute("name")) return;
        autoFieldIdSeed += 1;
        field.setAttribute("id", `auto-form-field-${autoFieldIdSeed}`);
      });
    };

    ensureFormFieldIdentity(document);

    if (typeof MutationObserver === "undefined") return undefined;
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches?.(fieldSelector)) {
            ensureFormFieldIdentity(node.parentElement || node);
            return;
          }
          ensureFormFieldIdentity(node);
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // 只在浏览器端执行
    if (typeof window === "undefined") return;
    if (
      window.location?.hostname === "localhost" ||
      window.location?.hostname === "127.0.0.1"
    ) {
      return;
    }
    const hasManualCountry = localStorage.getItem("lukkey_selected_country");
    fetch("https://ipapi.co/json/")
      .then((res) => res.json())
      .then((data) => {
        if (!hasManualCountry && data.country_code) {
          const market = getMarketByCountry(data.country_code);
          setSelectedCountry(market.countryCode);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem("lukkey_selected_country", selectedCountry);
  }, [selectedCountry]);
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [lukkeyVariantOptions, setLukkeyVariantOptions] = useState([]);
  const [lukkeyPricingSnapshot, setLukkeyPricingSnapshot] = useState([]);
  const lukkeyVariantsSignatureRef = useRef("");
  const lukkeyPricingSignatureRef = useRef("");

  const serializeLukkeyItems = useCallback((items = []) => {
    if (!Array.isArray(items) || items.length === 0) return "[]";
    return JSON.stringify(
      items.map((item) => ({
        cartKey: item?.cartKey || "",
        variantId: String(item?.variantId || ""),
        name: item?.name || "",
        color: item?.color || "",
        price: Number(item?.price || 0),
        currency: item?.currency || "",
      }))
    );
  }, []);

  const handleLukkeyVariantOptionsChange = useCallback(
    (nextOptions = []) => {
      const nextSignature = serializeLukkeyItems(nextOptions);
      if (lukkeyVariantsSignatureRef.current === nextSignature) return;
      lukkeyVariantsSignatureRef.current = nextSignature;
      setLukkeyVariantOptions(nextOptions);
    },
    [serializeLukkeyItems]
  );

  const handleLukkeyPricingSnapshotChange = useCallback(
    (nextSnapshot = []) => {
      const nextSignature = serializeLukkeyItems(nextSnapshot);
      if (lukkeyPricingSignatureRef.current === nextSignature) return;
      lukkeyPricingSignatureRef.current = nextSignature;
      setLukkeyPricingSnapshot(nextSnapshot);
    },
    [serializeLukkeyItems]
  );

  const addToCart = (item) => {
    const itemKind = isReserveItem(item) ? "reserve" : "product";
    const itemPrice = Number(item?.price || 0);
    const itemCurrency = item?.currency || "unknown";
    setCartItems((prev) => {
      const itemKey =
        item.cartKey ||
        `${item.variantId || "no-variant"}::${item.color || ""}::${item.name || "item"}`;
      const existingIndex = prev.findIndex(
        (entry) =>
          (entry.cartKey ||
            `${entry.variantId || "no-variant"}::${entry.color || ""}::${entry.name || "item"}`) ===
          itemKey
      );

      if (existingIndex === -1) {
        return [
          ...prev,
          {
            ...item,
            cartKey: itemKey,
            quantity: isReserveItem(item) ? 1 : item.quantity || 1,
          },
        ];
      }

      return prev.map((entry, index) =>
        index === existingIndex
          ? {
              ...entry,
              ...item,
              cartKey: itemKey,
              quantity: isReserveItem(item)
                ? 1
                : (entry.quantity || 1) + (item.quantity || 1),
            }
          : entry
      );
    });
    trackAnalyticsEvent("cart_add_item", {
      params: {
        cart_action: "add",
        cart_item_kind: itemKind,
        cart_item_currency: itemCurrency,
        cart_item_price: itemPrice,
        cart_has_reserve: itemKind === "reserve" ? "yes" : "no",
        cart_value_bucket: getCartValueBucket(itemPrice),
        entry_point: "lukkey_product",
      },
      clarityTags: {
        cart_last_add_type: itemKind,
        cart_last_add_currency: itemCurrency,
        cart_last_add_value_bucket: getCartValueBucket(itemPrice),
      },
    });
    setIsCartOpen(true);
  };

  const updateCartQuantity = (index, quantity) => {
    const targetItem = cartItems[index];
    trackAnalyticsEvent("cart_quantity_change", {
      params: {
        cart_action: "update_quantity",
        cart_item_kind: isReserveItem(targetItem) ? "reserve" : "product",
        cart_item_currency: targetItem?.currency || "unknown",
        cart_previous_quantity: targetItem?.quantity || 1,
        cart_next_quantity: isReserveItem(targetItem)
          ? 1
          : Math.max(1, quantity),
      },
    });
    setCartItems((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              quantity: isReserveItem(item)
                ? 1
                : Math.max(1, quantity),
            }
          : item
      )
    );
  };

  const removeCartItem = (index) => {
    const targetItem = cartItems[index];
    trackAnalyticsEvent("cart_remove_item", {
      params: {
        cart_action: "remove",
        cart_item_kind: isReserveItem(targetItem) ? "reserve" : "product",
        cart_item_currency: targetItem?.currency || "unknown",
        cart_previous_quantity: targetItem?.quantity || 1,
      },
    });
    setCartItems((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  useEffect(() => {
    if (!lukkeyPricingSnapshot.length) return;

    const byCartKey = new Map();
    const byVariantId = new Map();
    lukkeyPricingSnapshot.forEach((item) => {
      if (item?.cartKey) {
        byCartKey.set(item.cartKey, item);
      }
      if (item?.variantId) {
        byVariantId.set(String(item.variantId), item);
      }
    });

    setCartItems((prev) => {
      let changed = false;
      const next = prev.map((item) => {
        const latest =
          byCartKey.get(item.cartKey) ||
          (item.variantId ? byVariantId.get(String(item.variantId)) : null);

        if (!latest) return item;

        const merged = {
          ...item,
          ...latest,
          quantity: isReserveItem(item) ? 1 : item.quantity || 1,
          details: item.details,
          detailsTitle: item.detailsTitle,
          detailsSummary: item.detailsSummary,
        };

        if (
          merged.price !== item.price ||
          merged.currency !== item.currency ||
          merged.name !== item.name ||
          merged.variantId !== item.variantId ||
          merged.image !== item.image ||
          merged.color !== item.color
        ) {
          changed = true;
        }

        return merged;
      });

      return changed ? next : prev;
    });
  }, [lukkeyPricingSnapshot]);

  const renderLukkeyPage = () => (
    <Lukkey
      addToCart={addToCart}
      onOpenCart={() => setIsCartOpen(true)}
      onProductVariantsChange={handleLukkeyVariantOptionsChange}
      onPricingSnapshotChange={handleLukkeyPricingSnapshotChange}
      selectedCountry={selectedCountry}
      onCountryChange={setSelectedCountry}
    />
  );

  const renderPublicRoutes = () => (
    <>
      <Route
        index
        element={
          <Home
            selectedCountry={selectedCountry}
            onCountryChange={setSelectedCountry}
            analyticsConsent={analyticsConsent}
          />
        }
      />
      <Route path="privacy" element={<PrivacyPolicy />} />
      <Route path="terms-of-service" element={<TermsOfService />} />
      <Route path="warranty-policy" element={<WarrantyPolicy />} />
      <Route path="compliance" element={<Compliance />} />
      <Route path="app" element={<AppDownload />} />
      <Route path="about-us" element={<AboutUs />} />
      <Route path="help-center" element={<HelpCenter />} />
      <Route path="help-center/:slug" element={<AcademyArticle />} />
      <Route path="support-request" element={<WarrantySupport />} />
      <Route
        path="international-delivery"
        element={<Navigate to="../shipping/page/1" replace />}
      />
      <Route
        path="international-delivery/page/:page"
        element={<ShippingAliasRedirect />}
      />
      <Route
        path="shipping-worldwide"
        element={<Navigate to="../shipping/page/1" replace />}
      />
      <Route
        path="shipping-worldwide/page/:page"
        element={<ShippingAliasRedirect />}
      />
      <Route path="shipping" element={<Navigate to="page/1" replace />} />
      <Route path="shipping/page/:page" element={<InternationalDelivery />} />
      <Route path="social" element={<Community />} />
      <Route path="community" element={<Navigate to="../social" replace />} />
      <Route path="media-kit" element={<MediaKit selectedCountry={selectedCountry} onCountryChange={setSelectedCountry} />} />
      <Route
        path="3drender/*"
        element={
          <SensitiveRouteShield>
            <EmailOtpProtectedRoute
              allowedEmails={["18038163520@163.com"]}
            >
              <ThreeDRender />
            </EmailOtpProtectedRoute>
          </SensitiveRouteShield>
        }
      />
      <Route path="supported-crypto-assets" element={<SupportedCryptoAssets />} />
      <Route path="partners" element={<PartnerPrograms />} />
      <Route
        path="affiliate/apply"
        element={<PartnerInquiry forcedProgram="affiliate" canonicalPathOverride="/affiliate/apply" accentVariant="secondary" />}
      />
      <Route
        path="cobranding/apply"
        element={<PartnerInquiry forcedProgram="cobranding" canonicalPathOverride="/cobranding/apply" accentVariant="gold" />}
      />
      <Route
        path="co-branding/apply"
        element={<PartnerInquiry forcedProgram="cobranding" canonicalPathOverride="/co-branding/apply" accentVariant="silver" />}
      />
      <Route path="partners/:program/apply" element={<PartnerInquiry />} />
      <Route path="partners/apply" element={<PartnerInquiry />} />
      <Route path="co-branding" element={<CoBranding />} />
      <Route path="lukkey" element={renderLukkeyPage()} />
      <Route
        path="pre"
        element={
          <>
            <NoIndex />
            {renderLukkeyPage()}
          </>
        }
      />
      <Route path="demo" element={<Navigate to=".." replace />} />
      <Route
        path="lukkey-issues"
        element={
          <SensitiveRouteShield>
            <EmailOtpProtectedRoute>
              <LukkeyIssuesPage />
            </EmailOtpProtectedRoute>
          </SensitiveRouteShield>
        }
      />
      <Route
        path="workspace"
        element={
          <SensitiveRouteShield>
            <EmailOtpProtectedRoute>
              <LukkeyIssuesPage />
            </EmailOtpProtectedRoute>
          </SensitiveRouteShield>
        }
      />
      <Route
        path="issues"
        element={
          <SensitiveRouteShield>
            <EmailOtpProtectedRoute>
              <LukkeyIssuesPage />
            </EmailOtpProtectedRoute>
          </SensitiveRouteShield>
        }
      />
      <Route
        path="financial"
        element={
          <SensitiveRouteShield>
            <PasswordProtectedRoute password="lukkey2025">
              <FinancialModelPage />
            </PasswordProtectedRoute>
          </SensitiveRouteShield>
        }
      />
      <Route
        path="finance"
        element={
          <SensitiveRouteShield>
            <PasswordProtectedRoute password="lukkey2025">
              <FinancialModelPage />
            </PasswordProtectedRoute>
          </SensitiveRouteShield>
        }
      />
      <Route
        path="r2-files"
        element={
          <SensitiveRouteShield>
            <EmailOtpProtectedRoute allowedLevels={["publisher"]}>
              <R2ManagerPage />
            </EmailOtpProtectedRoute>
          </SensitiveRouteShield>
        }
      />
      <Route
        path="admin/auth"
        element={
          <SensitiveRouteShield>
            <EmailOtpProtectedRoute allowedLevels={["superadmin"]}>
              <AdminAuthPage />
            </EmailOtpProtectedRoute>
          </SensitiveRouteShield>
        }
      />
      <Route
        path="scm"
        element={
          <SensitiveRouteShield>
            <EmailOtpProtectedRoute allowedLevels={["superadmin", "scm", "developer"]}>
              <CostDistributionAdmin />
            </EmailOtpProtectedRoute>
          </SensitiveRouteShield>
        }
      />
      <Route
        path="barcode"
        element={
          <SensitiveRouteShield>
            <EmailOtpProtectedRoute allowedLevels={["superadmin", "scm", "developer"]}>
              <BarcodeGenerator />
            </EmailOtpProtectedRoute>
          </SensitiveRouteShield>
        }
      />
      <Route
        path="design"
        element={
          <SensitiveRouteShield>
            <EmailOtpProtectedRoute allowedLevels={["superadmin"]}>
              <InternalTypographyAudit />
            </EmailOtpProtectedRoute>
          </SensitiveRouteShield>
        }
      />
      <Route
        path="audit"
        element={
          <SensitiveRouteShield>
            <Navigate to="../design" replace />
          </SensitiveRouteShield>
        }
      />
      <Route
        path="ldesign"
        element={
          <SensitiveRouteShield>
            <Navigate to="../design" replace />
          </SensitiveRouteShield>
        }
      />
      <Route
        path="internal/typography-audit"
        element={
          <SensitiveRouteShield>
            <Navigate to="../design" replace />
          </SensitiveRouteShield>
        }
      />
      <Route
        path="brc20"
        element={
          <SensitiveRouteShield>
            <Brc20Inspector />
          </SensitiveRouteShield>
        }
      />
      <Route path="academy" element={<Navigate to=".." replace />} />
      <Route path="academy/:slug" element={<Navigate to=".." replace />} />
      <Route
        path="admin"
        element={
          <SensitiveRouteShield>
            <AcademyAdminPage />
          </SensitiveRouteShield>
        }
      />
      <Route
        path="subscribe/confirmed"
        element={
          <SensitiveRouteShield>
            <SubscribeConfirmed />
          </SensitiveRouteShield>
        }
      />
      <Route
        path="purchase/success"
        element={
          <SensitiveRouteShield>
            <PurchaseSuccess selectedCountry={selectedCountry} />
          </SensitiveRouteShield>
        }
      />
      <Route
        path="jaime"
        element={
          <SensitiveRouteShield>
            <EmailOtpProtectedRoute>
              <JaimeCalendar />
            </EmailOtpProtectedRoute>
          </SensitiveRouteShield>
        }
      />
      <Route path="*" element={<NotFound />} />
    </>
  );

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <RouteSeoHead />
      <ClarityRouteTracker analyticsConsent={analyticsConsent} />
      <Suspense fallback={null}>
        <GlobalButtonMotion />
      </Suspense>
      <SideCart
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={cartItems}
        onUpdateQuantity={updateCartQuantity}
        onRemoveItem={removeCartItem}
        variantOptions={lukkeyVariantOptions}
        onAddVariant={addToCart}
        selectedCountry={selectedCountry}
        countryOptions={MARKET_OPTIONS}
        onCountryChange={setSelectedCountry}
      />
      <Suspense
        fallback={
          <div className="min-h-screen bg-[#1D1D1D] text-white flex items-center justify-center">
            Loading...
          </div>
        }
      >
        <Routes>
          <Route path="/:locale?" element={<OptionalLocaleBoundary />}>
            {renderPublicRoutes()}
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
